<script setup lang="ts">
import merge from 'lodash-es/merge';
import type { FolderListItemModel, PageListItemModel } from '#layers/cms/app/pages/cms/management/pages/_runtime/services/PagesService';
import PagesService from '#layers/elearning/app/pages/cms/management/_runtime/services/PagesService';

defineOptions({
    name: 'WidgetIconSettings'
});

const { $t } = useLocalization();
const { $log } = useLogging();

const emit = defineEmits<{
    (e: 'update:modelValue', payload: any): void;
}>();

const props = defineProps<{
    modelValue: any;
    aliasFolderId?: number;
}>();

const folders = ref<FolderListItemModel[]>([]);
const pages = ref<PageListItemModel[]>([]);

const form = computed<FormType<any>>(() =>
{
    return props.modelValue;
});

const defaults = ref<any>({
    file: null,
    mode: PageModeEnum.Alias,
    folderId: 0,
    aliasId: null,
    externalUrl: ''
});

const pageModeOptions = computed((): Option[] =>
{
    return [
        { value: PageModeEnum.Alias, text: $t('[[[Link do wybranej strony serwisu]]]') },
        { value: PageModeEnum.Link, text: $t('[[[Link do zewnętrznej strony]]]') }
    ];
});

const pageMode = computed((): typeof PageModeEnum =>
{
    return PageModeEnum;
});

const loadFolders = async (): Promise<void> =>
{
    try
    {
        const response = (await PagesService.getFolders(form.value.websiteId, form.value.locale)).items.map(p => p.result);

        folders.value = [{
            id: 0,
            name: $t('[[[Wybierz katalog]]]'),
            locale: ''
        }].concat(response);
    }
    catch (ex)
    {
        folders.value = [];
        console.debug(ex);
    }
};

const loadPages = async (): Promise<void> =>
{
    form.value.wait();

    try
    {
        const response = await PagesService.getPagesWithoutElementsByFolder(form.value.settings.folderId);

        pages.value = [{
            id: null as number,
            name: '',
            option: $t('[[[Wybierz stronę]]]'),
            folderId: 0,
            module: '',
            pageMode: pageMode.value.Page,
            level: 0,
            hasChildren: false,
            position: 0,
            hasCustomLayout: false
        }].concat(response);
    }
    catch (ex)
    {
        handleException($log, ex, {
            0: (ex: any) => form.value.$errors.push('settings.aliasId', $t('[[[Nie udało się pobrać listy stron]]]'))
        });
    }
    finally
    {
        form.value.continue();
    }
};

const triggetModel = (): void =>
{
    emit('update:modelValue', form.value);
};

const validate = async (): Promise<boolean> =>
{
    if (form.value.settings.mode == PageModeEnum.Alias)
    {
        form.value.validate(schema => ({
            settings: schema.object({
                folderId: schema.string().nullable().required($t('[[[Wybranie katalogu jest wymagane]]')),
                aliasId: schema.string().nullable().required($t('[[[Wybranie strony jest wymagane]]'))
            })
        }));
    }
    else
    {
        form.value.validate(schema => ({
            settings: schema.object({
                externalUrl: schema.string().nullable().required($t('[[[Zewnętrzny link jest wymagany]]'))
            })
        }));
    }

    if (form.value.settings.file === null)
    {
        const errors: Record<string, string[]> = {};

        errors['settings.file'] = [$t('[[[Wybranie ikony jest wymagane.]]]')];
        form.value.$errors.record(errors);

        return false;
    }

    return true;
};

watch(() => form.value.settings, (value: any) =>
{
    triggetModel();
}, { deep: true });

watch(() => form.value.settings.folderId, async () =>
{
    await loadPages();
});

onBeforeMount(async (): Promise<void> =>
{
    await loadFolders();
    await loadPages();
    form.value.settings = merge(defaults.value, form.value.settings);
});

defineExpose({
    validate
});
</script>

<template>
    <div class="row">
        <div class="col-12">
            <IdeoFormGroup :label="$t('[[[Ikona]]]')" :invalid-feedback="form.$errors.first('settings.file')" :state="form.$errors.state('settings.file')" required>
                <IdeoFilePicker v-model="form.settings.file" name="settings.file" media="icons" />
            </IdeoFormGroup>
        </div>
        <div class="col-12">
            <IdeoFormGroup :label="$t('[[[Typ linku]]]')" :invalid-feedback="form.$errors.first('mode')" :state="form.$errors.state('mode')" required>
                <IdeoFormRadioGroup v-model="form.settings.mode" name="mode" :options="pageModeOptions" stacked />
            </IdeoFormGroup>
            <IdeoFormGroup v-if="form.settings.mode == pageMode.Alias" :label="$t('[[[Wybierz katalog]]]')" :invalid-feedback="form.$errors.first('settings.folderId')" :state="form.$errors.state('settings.folderId')" required>
                <IdeoFormSelect v-model="form.settings.folderId" name="settings.folderId" :options="folders" value-field="id" text-field="name" />
            </IdeoFormGroup>
            <IdeoFormGroup v-if="form.settings.mode == pageMode.Alias" :label="$t('[[[Link do strony]]]')" :invalid-feedback="form.$errors.first('settings.aliasId')" :state="form.$errors.state('settings.aliasId')" required>
                <IdeoFormSelect v-model="form.settings.aliasId" name="settings.aliasId" :options="pages" value-field="id" text-field="option" />
            </IdeoFormGroup>
            <IdeoFormGroup v-if="form.settings.mode == pageMode.Link" :label="$t('[[[Adres strony]]]')" :invalid-feedback="form.$errors.first('settings.externalUrl')" :state="form.$errors.state('settings.externalUrl')" required>
                <IdeoFormInput v-model="form.settings.externalUrl" type="text" name="settings.externalUrl" />
            </IdeoFormGroup>
        </div>
    </div>
</template>
